import type { FetchQueryOptions, QueryOptions } from '@tanstack/vue-query'
import type { LocationQuery, RouteLocationRaw, RouteParamsRawGeneric } from 'vue-router'
import type { Section } from '~/types/section'
import type { Locale } from 'vue-i18n'
// Nechat zde tento import. Autoimport nefunguje správně v adminu
import { getPermanentRoute } from '@@/shared-components/composables/redirects'
import { locales } from '@@/shared-components/locales/index'
import { Categories } from '@@/shared-components/categories'

export type BaseRedirectRouteValueCommon = {
  query?: (query: LocationQuery, data?: Record<string, unknown>) => Record<string, string>
  params?: (query: LocationQuery, data?: Record<string, unknown>) => RouteParamsRawGeneric
  hash?: (query: LocationQuery, data?: Record<string, unknown>) => string
}

// Varianta s queryOption a povinným linkParser
export type WithQueryOption = {
  queryOption: (data: {
    dataLang: ValueOf<typeof locales>
    id?: string
    hasPreview?: boolean
    expires?: string
    signature?: string
    origin?: string
    query: LocationQuery
  }) => QueryOptions | FetchQueryOptions
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  linkParser: (
    data: any,
    query: LocationQuery
  ) => {
    type: string
    key: { name: string; value: string } | null
    [key: string]: unknown
  }
}

// Varianta bez queryOption ani linkParseru
export type WithoutQueryOption = {
  queryOption?: undefined
  linkParser?: undefined
}

export type BaseRedirectRouteValue =
  | (BaseRedirectRouteValueCommon & WithQueryOption)
  | (BaseRedirectRouteValueCommon & WithoutQueryOption)

type RedirectRoutePage = BaseRedirectRouteValue & {
  pageName: string | ((query?: LocationQuery) => string)
  customAction?: never
}

type RedirectRouteCustom = BaseRedirectRouteValue & {
  pageName?: never
  customAction: (
    query: LocationQuery,
    locale: Locale
  ) => Promise<ReturnType<typeof navigateTo> | void> | ReturnType<typeof navigateTo> | void
}

export type RedirectRouteValue = RedirectRoutePage | RedirectRouteCustom

export const ARTICLE_DETAIL_KEY = 'article-detail'

export type RedirectRouteKeys =
  | 'eshop:branch:list'
  | 'eshop:basket:hydrate'
  | 'eshop:home'
  | 'eshop:order:detail'
  | 'eshop:account:recovery'
  | 'eshop:product:detail'
  | 'eshop:article:detail-key'
  | 'eshop:article:detail'
  | 'eshop:loyaltyProgram:register'
  | 'eshop:branch:detail'
  | 'eshop:rate'
  | 'eshop:rate:positive'
  | 'eshop:rate:negative'
  | 'eshop:product:list'
  | 'eshop:article:list'
  | 'eshop:login'
  | 'eshop:logout'
  | 'eshop:register'
  | 'eshop:section'
  | 'eshop:section:detail'
  | 'eshop:seo:city'

export type RedirectRoutesRegistry = {
  [key in RedirectRouteKeys]: RedirectRouteValue
}

export interface ExtendedRedirectRoutesRegistry {
  [key: string]: RedirectRouteValue
}

const eshopRatingValue: RedirectRouteValue = {
  customAction: async (query: LocationQuery, locale: Locale) => {
    const { $localePath, runWithContext } = useNuxtApp()
    if (query.code && query.type) {
      const [, , rate] = (query.action as string).split(':')
      // Musíme uživatele přesměrovat na rozcesník 👍👎 jelikož musí projít přes ReCaptchu
      return runWithContext(() =>
        navigateTo({
          path: $localePath(`/r${query.type === 'buyer' ? 'b' : 'g'}-${query.code}`, locale),
          query:
            rate === 'negative'
              ? {
                  rateNegative: 1,
                }
              : undefined,
        })
      )
    }
  },
}

export const accountRecoveryValue: RedirectRouteValue = {
  pageName: 'reset-password',
  query: (query: LocationQuery) => ({
    token: query.token as string,
    email: query.email as string,
    new: query.new as string,
  }),
}

const sectionDetailValue = {
  pageName: 'section-detail',
  queryOption: ({ id, hasPreview, expires, signature, origin, dataLang }) =>
    sectionQuery({
      sectionName: toRef(() => id as string),
      query: toRef(() => ({
        withItems: 0,
      })),
      hasPreview: hasPreview
        ? { expires: expires as string, signature: signature as string }
        : undefined,
      dataLang: toRef(() => dataLang),
      origin,
    }) as QueryOptions,
  linkParser: (data: { data: Section }) => ({
    type: data?.data?.tag?._link?.type.value as string,
    key: data?.data?.tag?._link?.key
      ? { name: data?.data?.tag?._link?.key.name, value: data?.data?.tag?._link?.key.value }
      : null,
    id: data?.data?.tag?._link?.identifier,
    slug: data?.data?.tag?._link?.slug,
  }),
}

const categoryDetailValue: RedirectRouteValue = {
  pageName: 'category-detail',
  queryOption: ({ id, hasPreview, expires, signature, origin }) => {
    const { dataLang } = useLocale()
    const { channel } = useChannel()
    return publicTagQuery({
      id: id as string,
      dataLang,
      channel,
      hasPreview: hasPreview
        ? { expires: expires as string, signature: signature as string }
        : undefined,
      origin,
    }) as QueryOptions
  },
  linkParser: (data: { data: Tag }) => ({
    type: data?.data?._link.type.value,
    key: data?.data?._link.key
      ? { name: data?.data?._link.key.name, value: data?.data?._link.key.value }
      : null,
    id: data?.data?._link?.identifier,
    slug: data?.data?._link?.slug,
  }),
}

export class Redirect {
  private static instance: Redirect
  public static getInstance(): Redirect {
    if (!Redirect.instance) {
      Redirect.instance = new Redirect()
    }
    return Redirect.instance
  }

  public redirectParser = <T>(route: RouteLocationRaw | undefined, _: T | undefined) => route

  private static defaultRedirectRoutes: RedirectRoutesRegistry = {
    'eshop:home': {
      pageName: 'home',
    },
    'eshop:order:detail': {
      pageName: 'public-order-detail',
      params: (query: LocationQuery) => ({ guid: query.order as string }),
    },
    'eshop:account:recovery': accountRecoveryValue,
    'eshop:product:detail': {
      pageName: 'product-detail',
      queryOption: ({ id, hasPreview, expires, signature, origin, dataLang }) => {
        const { currency } = useCurrency()
        return publicProductQuery({
          id: id as string,
          currency,
          dataLang: toRef(() => dataLang),
          hasPreview: hasPreview
            ? { expires: expires as string, signature: signature as string }
            : undefined,
          origin,
        }) as QueryOptions
      },
      linkParser: (data) => ({
        type: data?.data?._link?.type.value,
        key: data?.data?._link.key
          ? { name: data?.data?._link.key.name, value: data?.data?._link.key.value }
          : null,
        id: data?.data?._link?.identifier,
        slug: data?.data?._link?.slug,
      }),
    },
    'eshop:article:detail-key': {
      customAction: async (query: LocationQuery, locale: Locale) => {
        const { $localePath } = useNuxtApp()
        if (!query.key) {
          return createError({
            statusCode: 404,
            statusMessage: 'Not Found',
            message: 'Missing key',
          })
        }
        const route = getPermanentRoute({ key: { name: '', value: query.key as string } })
        if (!route) {
          return createError({
            statusCode: 404,
            statusMessage: 'Not Found',
            message: `Permanent route for key ${query.key} was not found`,
          })
        }
        return navigateTo($localePath(route, locale))
      },
    },
    'eshop:article:detail': {
      pageName: ARTICLE_DETAIL_KEY,
      queryOption: ({ id, hasPreview, expires, signature, origin, dataLang }) => {
        return articleQuery({
          articleName: id as string,
          dataLang: toRef(() => dataLang),
          hasPreview: hasPreview
            ? { expires: expires as string, signature: signature as string }
            : undefined,
          origin,
        }) as QueryOptions
      },
      linkParser: (data) => {
        const link = {
          id: data?._link?.identifier,
          slug: data?._link?.slug,
          type: 'article',
        }
        if (data?._link?.key?.value?.split('.')?.[0] !== 'seo') {
          link.type = data?._link?.type.value
          link.key = data?._link.key
            ? { name: data?._link.key.name, value: data?._link.key.value }
            : null
        }
        return link
      },
    },
    'eshop:loyaltyProgram:register': {
      pageName: 'loyalty-program',
    },
    'eshop:basket:hydrate': {
      pageName: 'basket-summary',
      query: (query: LocationQuery) => ({
        'hydrate-basket': query.bsk as string,
      }),
    },
    'eshop:branch:list': {
      pageName: 'contact-us',
    },
    'eshop:branch:detail': {
      pageName: 'branch-detail',
      params: (query: LocationQuery) => ({ id: query.id as string }),
      queryOption: ({ id, hasPreview, expires, signature, origin, dataLang }) =>
        branchQuery({
          id: toRef(() => id as string),
          hasPreview: hasPreview
            ? { expires: expires as string, signature: signature as string }
            : undefined,
          dataLang: toRef(() => dataLang),
          origin,
        }) as QueryOptions,
      linkParser: ({ data }: { data: Branch }) => ({
        type: data._link?.type.value as string,
        key: data._link?.key ? { name: data._link?.key.name, value: data._link?.key.value } : null,
        id: data._link?.identifier,
        slug: data._link?.slug,
      }),
    },
    'eshop:product:list': {
      pageName: 'product-list',
    },
    'eshop:article:list': {
      pageName: 'article-list',
    },
    'eshop:login': {
      pageName: 'login',
    },
    'eshop:rate': eshopRatingValue,
    'eshop:rate:positive': eshopRatingValue,
    'eshop:rate:negative': eshopRatingValue,
    'eshop:logout': {
      pageName: 'logout',
    },
    'eshop:register': {
      pageName: 'register',
    },
    'eshop:section': sectionDetailValue,
    'eshop:section:detail': sectionDetailValue,
    'eshop:seo:city': {
      pageName: 'cities-id',
      queryOption: ({ id, dataLang }) =>
        publicCityQuery({ id: toRef(() => id as string), dataLang }) as QueryOptions,
      linkParser: ({
        data,
      }: {
        data: { article: Article; wildcards: { [key: string]: unknown } }
      }) => {
        const dataWithWildcards = JSON.parse(JSON.stringify(data?.article ?? {})) as Article
        return {
          type: dataWithWildcards._link?.type.value as string,
          key: dataWithWildcards._link?.key
            ? { name: dataWithWildcards._link?.key.name, value: dataWithWildcards._link?.key.value }
            : null,
          // v _link identifier je ID článku a né města
          id: data.wildcards.id as string,
          slug: dataWithWildcards._link?.slug,
        }
      },
    },
  }
  private static extendedRedirectRoutes: ExtendedRedirectRoutesRegistry = {}
  private static extendedPernamentRoutes: { [type: string]: string } = {}

  getDefaultRedirectRoutes = () => {
    return {
      ...Redirect.defaultRedirectRoutes,
      ...Object.fromEntries(
        Categories.getInstance()
          .getPreviewActions()
          .map((action) => [action, categoryDetailValue])
      ),
    }
  }

  extendPernamentRoutes = (routes: { [type: string]: string }) => {
    return (Redirect.extendedPernamentRoutes = routes)
  }

  extendRedirectRoutes = (routes: ExtendedRedirectRoutesRegistry) => {
    return (Redirect.extendedRedirectRoutes = routes)
  }

  getExtendedRedirectRoute = (route: string) => {
    return Redirect.extendedRedirectRoutes[route]
  }

  getExtendedRedirectRoutes = () => {
    return Redirect.extendedRedirectRoutes
  }

  getExtendedPernamentRoute = (route: string) => {
    return Redirect.extendedPernamentRoutes[route]
  }

  getExtendedPernamentRoutes = () => {
    return Redirect.extendedPernamentRoutes
  }

  setRedirectParser = (
    p: <T>(route: RouteLocationRaw | undefined, data: T) => RouteLocationRaw | undefined
  ) => {
    this.redirectParser = p
  }
}
